import React, { useContext, useEffect, useRef } from 'react'
import { SkeletonElement } from '@notino/react-styleguide'
import { google, outlook, office365, msTeams, yahoo, aol, outlookMobile, office365Mobile } from 'calendar-link'
import * as ics from 'ics'
import dayjs from 'dayjs'
import * as Sentry from '@sentry/react'
import { Portal } from 'react-portal'
import { useIntl } from 'react-intl'

// utils
import { getLinkAttributes, isMobileDevice, showNotifications } from '../../utils/helper'
import { ICS_DATE_FORMAT, MSG_TYPE } from '../../utils/enums'
import { getIntl } from '../../utils/intl'
import { AppContext } from '../../utils/appProvider'

// assets
import GoogleCalendarIcon from '../../assets/icons/GoogleCalendarIcon'
import Office365Icon from '../../assets/icons/Office365Icon'
import OutlookIcon from '../../assets/icons/OutlookIcon'
import MsTeamsIcon from '../../assets/icons/MsTeamsIcon'
import YahooIcon from '../../assets/icons/YahooIcon'
import CalendarIcon from '../../assets/icons/CalendarIcon'
import AppleIcon from '../../assets/icons/AppleIcon'

// hooks
import useMessages from '../../hooks/useMessages'

// styles
import * as SC from './AddToCalendarModalStyles'
import Modal, { ModalModel } from '../../atoms/Modal/Modal'

type CalendarFile = {
	/** Format: date-time */
	start: string
	/** Format: date-time */
	end: string
	UID: string
	description: string
	summary: string
}

type Props = {
	show: boolean
	onClose: () => void
	calendarFile: CalendarFile
	reservationID: string
}

type CalendarLinkItemType = {
	type: 'link'
	link: string | undefined
}

type CalendarButtonItemType = {
	type: 'button'
	onClick: () => void
}

type CalendarItem = {
	title: string
	icon: React.ReactNode
} & (CalendarLinkItemType | CalendarButtonItemType)

const onDownloadIcsFile = async (calendarFile: CalendarFile, reservationID: string, container: HTMLElement | null) => {
	const intl = getIntl()
	const filename = `reservation-${calendarFile.UID}.ics`

	try {
		if (!container) {
			throw new Error('Calendar download container is not available')
		}

		const file = await new Promise<File>((resolve, reject) => {
			ics.createEvent(
				{
					title: calendarFile.description,
					description: calendarFile.summary,
					start: dayjs(calendarFile.start).utc().format(ICS_DATE_FORMAT),
					end: dayjs(calendarFile.end).utc().format(ICS_DATE_FORMAT),
					uid: calendarFile.UID
				},
				(error, value) => {
					if (error) {
						reject(error)
					}

					resolve(new File([value], filename, { type: 'text/calendar' }))
				}
			)
		})
		const url = URL.createObjectURL(file)

		// trying to assign the file URL to a window could cause cross-site
		// issues so this is a workaround using HTML5.
		// Append inside the modal so the synthetic click stays in the focus trap;
		// document.body is treated as an outside click and cancelled.
		const anchor = document.createElement('a')
		anchor.href = url
		anchor.download = filename
		anchor.setAttribute('aria-hidden', 'true')
		anchor.tabIndex = -1
		anchor.style.display = 'none'

		container.appendChild(anchor)
		anchor.click()
		container.removeChild(anchor)

		URL.revokeObjectURL(url)
	} catch (error) {
		showNotifications([{ type: MSG_TYPE.ERROR, message: intl.formatMessage({ id: 'We could not create your calendar event. Please try again.' }) }])
		// eslint-disable-next-line no-console
		Sentry.captureException(new Error(`Error creating calendar event .ics file with an ID ${reservationID}`, { cause: error }))
	}
}

const getCalendarLink = (calendarFile: CalendarFile, calendarType: 'google' | 'outlook' | 'office365' | 'yahoo' | 'aol' | 'msTeams'): string | undefined => {
	const calendarEvent = {
		title: calendarFile.description,
		description: calendarFile.summary,
		start: calendarFile.start,
		end: calendarFile.end
	}

	let link: string | undefined

	switch (calendarType) {
		case 'google':
			link = google(calendarEvent)
			break
		case 'outlook':
			link = isMobileDevice() ? outlookMobile(calendarEvent) : outlook(calendarEvent)
			break
		case 'office365':
			link = isMobileDevice() ? office365Mobile(calendarEvent) : office365(calendarEvent)
			break
		case 'yahoo':
			link = yahoo(calendarEvent)
			break
		case 'aol':
			link = aol(calendarEvent)
			break
		case 'msTeams':
			link = msTeams(calendarEvent)
			break
		default:
			break
	}

	return link
}

const AddToCalendarModal = (props: Props) => {
	const { show, onClose, calendarFile, reservationID } = props
	const { messages } = useMessages()
	const { productionEnvironment } = useContext(AppContext)
	const { locale } = useIntl()

	const [isLoading, setIsLoading] = React.useState(true)
	const downloadContainerRef = useRef<HTMLDivElement>(null)

	/**
	 * Loading because this is just a client side Modal component, where we are working with window object which is not available during SSR.
	 */
	useEffect(() => {
		setIsLoading(false)
	}, [])

	const calendarItems: CalendarItem[] = [
		{
			type: 'button',
			title: 'Apple',
			icon: <AppleIcon />,
			onClick: () => onDownloadIcsFile(calendarFile, reservationID, downloadContainerRef.current)
		},
		{
			type: 'link',
			title: 'Google',
			icon: <GoogleCalendarIcon />,
			link: getCalendarLink(calendarFile, 'google')
		},
		{
			type: 'link',
			title: 'Microsoft Office 365',
			icon: <Office365Icon />,
			link: getCalendarLink(calendarFile, 'office365')
		},
		{
			type: 'link',
			title: 'Outlook',
			icon: <OutlookIcon />,
			link: getCalendarLink(calendarFile, 'outlook')
		},
		{
			type: 'link',
			title: 'Microsoft Teams',
			icon: <MsTeamsIcon />,
			link: getCalendarLink(calendarFile, 'msTeams')
		},
		{
			type: 'link',
			title: 'Yahoo',
			icon: <YahooIcon />,
			link: getCalendarLink(calendarFile, 'yahoo')
		},
		{
			type: 'button',
			title: messages['Download .ics file'],
			icon: <CalendarIcon />,
			onClick: () => onDownloadIcsFile(calendarFile, reservationID, downloadContainerRef.current)
		}
	]

	const loadingSkeletons = Array.from({ length: calendarItems.length }, (_, index) => (
		<SC.ElementsListItem key={index}>
			<SC.ElementsListItemSkeleton>
				<SC.SkeletonIconElement width={'16px'} height={'16px'} />
				<SkeletonElement width='100%' height='20px' />
			</SC.ElementsListItemSkeleton>
		</SC.ElementsListItem>
	))

	return (
		<Portal>
			<Modal show={show} onClose={onClose} size={ModalModel.ModalSizes.small} noBorders centerVertically ariaLabel={messages['Add to calendar']}>
				<SC.ModalHeader>{messages['Add to calendar']}</SC.ModalHeader>
				<SC.ModalMain ref={downloadContainerRef}>
					<SC.ElementsList>
						{isLoading
							? loadingSkeletons
							: calendarItems.map((item, index) => {
									if (item.type === 'link') {
										return (
											<SC.ElementsListItem key={index}>
												<SC.ElementsListItemLink
													href={item.link}
													{...getLinkAttributes(item.link || '', productionEnvironment, locale)}
													onClick={() => {
														if (!item.link) {
															showNotifications([
																{
																	type: MSG_TYPE.ERROR,
																	message: messages['We could not create your calendar event. Please try again.']
																}
															])
															Sentry.captureException(
																new Error(`Error creating calendar event .ics file with an ID ${reservationID}`)
															)
														}
													}}
												>
													{item.icon} {item.title}
												</SC.ElementsListItemLink>
											</SC.ElementsListItem>
										)
									}

									return (
										<SC.ElementsListItem key={index}>
											<SC.ElementsListItemButton onClick={item.onClick}>
												{item.icon} {item.title}
											</SC.ElementsListItemButton>
										</SC.ElementsListItem>
									)
								})}
					</SC.ElementsList>
				</SC.ModalMain>
			</Modal>
		</Portal>
	)
}

export default AddToCalendarModal
